// src/hooks/useDeliveryChargeCalculation.js
// Extracted from useOrderForm.js's Effect 1 / Effect 2 delivery-charge logic (Stage 8), so the
// offline-sales courier-fulfilled order form can reuse the exact same calculation engine.
//
// Unlike the original inline effects (which wrote straight into useOrderForm.js's `formData` via
// setFormData), this hook owns its own state and returns { deliveryCharge, isCalculatingDelivery }
// for the caller to consume/assign as needed.
import { useState, useEffect, useRef } from 'react';
import { getDeliveryCharge } from '../services/api/deliveryCharges';

// Compute delivery charge factoring in weight-based extra charges
export const computeDeliveryWithWeight = (base, extraPerKg, weight) => {
  const b = parseFloat(base) || 0;
  const e = parseFloat(extraPerKg) || 0;
  const w = parseFloat(weight) || 1;
  return (w > 1 && e > 0) ? b + (Math.ceil(w) - 1) * e : b;
};

/**
 * @param {object} params
 * @param {number|null} params.shopId
 * @param {number|null} params.cityId
 * @param {number} params.weight
 * @param {Array} params.orderItems
 * @param {boolean} [params.disableAutoCalc] - Suppresses ALL automatic delivery charge
 *   recalculation from the base-fetch step (used by EditOrderPage). The weight/override-driven
 *   recalculation in the second effect still runs regardless, matching the original behavior.
 * @param {(orderItems: Array) => boolean} [params.hasSkipFetchOverride] - When true, skips the
 *   base-charge API fetch entirely (an override already present makes it moot).
 * @param {(orderItems: Array) => number|null} [params.resolveOverride] - Returns an override
 *   delivery charge to use instead of the base+weight calculation, or null to fall through.
 */
const useDeliveryChargeCalculation = ({
  shopId,
  cityId,
  weight,
  orderItems,
  disableAutoCalc = false,
  hasSkipFetchOverride,
  resolveOverride,
}) => {
  // null = "not yet calculated" (distinct from 0, a real computed/override charge) — lets
  // consumers avoid clobbering an already-loaded value (e.g. EditOrderPage) before any real
  // calculation has actually happened for this render.
  const [deliveryCharge, setDeliveryCharge] = useState(null);
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);

  // Tracks the last API-fetched base delivery charge so the override effect
  // can compare against it without triggering extra API calls.
  const baseDeliveryChargeRef = useRef(null);
  // Tracks the extraChargePerKg from the last API-fetched delivery charge.
  const extraChargePerKgRef = useRef(null);

  // ── Effect 1: Fetch base delivery charge from API when shop or city changes ──
  useEffect(() => {
    const fetchBaseDeliveryCharge = async () => {
      if (!shopId || !cityId) return;
      if (hasSkipFetchOverride?.(orderItems)) return;

      try {
        setIsCalculatingDelivery(true);
        const response = await getDeliveryCharge(shopId, cityId);
        const { amount, extraChargePerKg = 0 } = response.deliveryCharge;
        baseDeliveryChargeRef.current = amount;
        extraChargePerKgRef.current = extraChargePerKg;
        if (!disableAutoCalc) {
          setDeliveryCharge(computeDeliveryWithWeight(amount, extraChargePerKg, weight));
        }
      } catch (error) {
        console.error('Failed to calculate delivery charge:', error);
        baseDeliveryChargeRef.current = 0;
        extraChargePerKgRef.current = 0;
        if (!disableAutoCalc) {
          setDeliveryCharge(0);
        }
      } finally {
        setIsCalculatingDelivery(false);
      }
    };

    fetchBaseDeliveryCharge();
  // orderItems intentionally excluded — handled by Effect 2
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, cityId, disableAutoCalc]);

  // ── Effect 2: Apply / remove override, or recompute from weight ──
  useEffect(() => {
    if (!shopId || !cityId) return;

    const override = resolveOverride?.(orderItems);
    if (override != null) {
      setDeliveryCharge(prev => (prev === override ? prev : override));
      return;
    }

    const base = baseDeliveryChargeRef.current;
    const extraPerKg = extraChargePerKgRef.current;
    if (base !== null) {
      const computed = computeDeliveryWithWeight(base, extraPerKg, weight);
      setDeliveryCharge(prev => (prev === computed ? prev : computed));
    }
    // If base is still null (city/shop not yet resolved), Effect 1 will set it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems, weight, disableAutoCalc]);

  return { deliveryCharge, isCalculatingDelivery };
};

export default useDeliveryChargeCalculation;
